(ns app.front.view.group.admin.form-schemas.util
  (:require [app.domain.form.form-editor-util :as form-editor-util]
            [app.domain.form.schema :as form-schema]
            [app.front.view.group.admin.subs :as gas]
            [clojure.core.rrb-vector :as fv]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.feature-flags :as ff]
            [editscript.core :as e]
            [potpuri.core :as p]))


;; Elasticsearch has limit of 512 for the key.
;; Keys are prefixed by the municipality group code (e.g. US-PA4202731472-B_) so 512 cannot be used directly.
(def field-key-max-length 480)


(defn only-icon-button [opts]
  (cc/button
    (merge {:kind          :ghost
            :size          :small
            :icon-position :right
            :class-name    "only-icon-button"}
           opts)
    ""))


(defn insert [v pos val]
  (fv/catvec (conj (fv/subvec v 0 pos) val) (fv/subvec v pos)))


(defn drop-index [v pos]
  (into (subvec v 0 pos) (subvec v (inc pos))))


(def common-child-types-for-form-row
  [:input
   :number-input
   :textarea
   :select
   :radio-button-group
   :checkbox-group
   :date-picker
   :time-picker
   ;; Tables are disabled because editing a table-element might break existing forms that use the schema
   ;; :table
   :span
   :p
   :h2
   :structured-list
   :personal-id])


(def schema-type-specific-child-types-for-form-row
  {:form.schema.type/role-specific    #{:signature}
   :form.schema.type/general          #{:signature}
   :form.schema.type/pre-consultation #{:signature}
   :form.schema.type/sign-off         #{:sign-off/signatures}})

(defn- schema-type-specific-child-types []
  (p/assoc-if schema-type-specific-child-types-for-form-row
              :form.schema.type/execution (when (ff/enabled? :feature-flag.id/signatures-in-execution-forms)
                                            #{:signature})))

(def child-types-for-form-row
  (->> schema-type-specific-child-types-for-form-row
       vals
       (apply concat)
       (concat common-child-types-for-form-row)
       distinct))


(def field-types-for-conditions
  #{:select :radio-button-group :checkbox})


(def common-attrs-for-scalar-type
  [:description
   :field
   :label
   :placeholder
   :form.field/required
   :condition
   :form.field/integration-field-id
   :form.field/ecm-integration-field-id])


(def disabled-attrs-for-existing-form-field
  #{:description :field :unit :unit-label})


(def required-attrs-when-present [:field :label :text :value])


(def container-types [:section :subsection :form-row])


(def child-types-for-table [:input :checkbox :number-input :textarea :date-picker])

(def child-types-for-list-row [:input :checkbox :number-input :textarea :span :p :date-picker])

(def locales-sub ::gas/allowed-locales)

(def config-for-types
  {:form {:possible-child-types [:section]
          :equivalent-types     []
          :editable-attrs       [:condition]}

   :section {:possible-child-types [:form-row :subsection]
             :equivalent-types     []
             :editable-attrs       [:label :condition]}

   :subsection {:possible-child-types [:form-row]
                :equivalent-types     []
                :editable-attrs       [:label :condition]}

   :form-row {:possible-child-types child-types-for-form-row
              :equivalent-types     []
              :editable-attrs       [:class-name :condition :number-of-columns]}

   :checkbox-group {:possible-child-types [:checkbox]
                    :equivalent-types     []
                    :editable-attrs       [:form.field/required :label :condition]}

   :checkbox {:possible-child-types []
              :equivalent-types     []
              :editable-attrs       [:description :field :label :condition]}

   :radio-button-group {:possible-child-types [:radio-button]
                        :equivalent-types     []
                        :editable-attrs       [:description
                                               :field
                                               :label
                                               :form.field/required
                                               :condition
                                               :default-value-source
                                               :form.field/integration-field-id
                                               :form.field/ecm-integration-field-id]}

   :radio-button {:possible-child-types []
                  :equivalent-types     []
                  :editable-attrs       [:value :label :condition]}

   :table {:possible-child-types child-types-for-table
           :equivalent-types     []
           :editable-attrs       [:description :field :condition :headers]
           :required-attrs       [:headers]}

   :span {:possible-child-types [:text]
          :equivalent-types     [:p :h2]
          :editable-attrs       [:condition]}

   :p {:possible-child-types [:text]
       :equivalent-types     [:span :h2]
       :editable-attrs       [:condition]}

   :h2 {:possible-child-types [:text]
        :equivalent-types     [:span :p]
        :editable-attrs       [:condition]}

   :date-picker {:possible-child-types []
                 :equivalent-types     []
                 :editable-attrs       [:description :field :label :form.field/required :condition :id]}

   :time-picker {:possible-child-types []
                 :equivalent-types     []
                 :editable-attrs       [:description :field :label :form.field/required :condition]}

   :input {:possible-child-types []
           :equivalent-types     [:textarea]
           :editable-attrs       (conj common-attrs-for-scalar-type :form.field/validator :default-value-source :phrases-picker?)}

   :personal-id {:possible-child-types []
                 :equivalent-types     []
                 :editable-attrs       common-attrs-for-scalar-type}

   :textarea {:possible-child-types []
              :equivalent-types     [:input]
              :editable-attrs       (conj common-attrs-for-scalar-type :phrases-picker? :rich-text?)}

   :number-input {:possible-child-types []
                  :equivalent-types     []
                  :editable-attrs       (conj common-attrs-for-scalar-type
                                              :step :unit :unit-label :form.field/validator :type)
                  :default-attributes   {:type :number}}

   :select {:possible-child-types []
            :equivalent-types     []
            :editable-attrs       (conj common-attrs-for-scalar-type
                                        :default-value-source
                                        :form.field/valid-values)
            :default-attributes   {:form.field/valid-values [""]
                                   :no-tr?                  true}
            :hidden-attributes    {:no-tr? true}}

   :signature {:possible-child-types []
               :equivalent-types     []
               :editable-attrs       [:description :field :text :form.field/required :required-role :signature-type :sign-off]
               :default-attributes   {:sign-off "Sign-off" :signature-type :click-sign}}

   :structured-list {:possible-child-types [:list-row]
                     :equivalent-types     []
                     :editable-attrs       [:headers]}

   :list-row {:possible-child-types child-types-for-list-row
              :equivalent-types     []
              :editable-attrs       []}

   :sign-off/declaration-section-v2 {:possible-child-types [:sign-off/declaration-subsection]}
   :sign-off/declaration-subsection {:possible-child-types [:text]
                                     :editable-attrs       [:label]}

   :file-input {:possible-child-types []
                :editable-attrs       [:description :field :label :condition]}

   :sign-off.rezoning/pre-consultation-information {:possible-child-types []
                                                    :editable-attrs       [:sign-off.rezoning/pre-consultation-info
                                                                           :sign-off.rezoning/pre-consultation-completed
                                                                           :sign-off.rezoning/pre-consultation-not-completed]}
   :sign-off.rezoning/declaration-section          {:possible-child-types []
                                                    :editable-attrs       [:sign-off.rezoning/declaration-section-title
                                                                           :sign-off.rezoning/declaration-section-label
                                                                           :sign-off.rezoning/declaration-section-content
                                                                           :sign-off.rezoning/applicant-signature
                                                                           :sign-off.rezoning/commissioner-signature
                                                                           :sign-off.rezoning/municipality
                                                                           :sign-off.rezoning/date
                                                                           :sign-off.rezoning/place-stamp-below]}
   :sign-off/signatures                            {:editable-attrs [:show-download-form? :class-name]}})


(defn rename-inherited-form-schema
  "Returns a map from the given entity
  and updates :form.schema/name with concatenating \" (superdomain-name)\" from superdomain entity"
  [form-schema superdomain]
  (-> (eu/->map form-schema)
      (update :form.schema/name str " (" (:group/name superdomain) ")")))


(defn form-el-type-is-text? [{:keys [type]}]
  (= :text type))


(defn form-el->non-proper-children [form-element]
  (form-editor-util/form-el->children form-el-type-is-text?
                                      form-element))


(defn form-el->proper-children [form-element]
  (form-editor-util/form-el->children (complement form-el-type-is-text?)
                                      form-element))


(defn editable-attrs-for-type [type]
  (get-in config-for-types [type :editable-attrs]))


(defn attr-editable-for-type? [type attr]
  (-> (editable-attrs-for-type type)
      set
      (contains? attr)))


(defn type->default-child-type [type]
  (->> (get-in config-for-types [type :possible-child-types])
       first))


(defn default-attributes-for-type [type]
  (get-in config-for-types [type :default-attributes]))

(defn hidden-attributes-for-type [type]
  (get-in config-for-types [type :hidden-attributes]))


(defn hide-uneditable-attrs-for-type [attrs type]
  (->> type
       hidden-attributes-for-type
       (filter (fn [[_ v]]
                 (= v true)))
       (map first)
       (apply dissoc attrs)))

(defn uneditable-attrs-for-type [attrs type]
  (-> attrs
      (hide-uneditable-attrs-for-type type)
      keys
      set
      ;; Separate text input displayed for integration id for groups that have integration enabled
      (disj :form.field/integration-field-id :form.field/ecm-integration-field-id)
      (set/difference (-> type editable-attrs-for-type set))))


(defn default-skeleton [type path order new-field child-id child-type]
  (let [child-path                   (conj path child-id)
        child-skeleton               (when child-type
                                       (default-skeleton child-type
                                                         child-path
                                                         0
                                                         true
                                                         (inc child-id)
                                                         nil))
        child-key-and-child-skeleton (when child-skeleton
                                       (hash-map (last child-path)
                                                 child-skeleton))]
    (->> {:id         (last path)
          :type       type
          :order      order
          :attributes (merge {} (default-attributes-for-type type))
          :path       path
          :new-field? new-field}
         (merge child-key-and-child-skeleton)
         (p/filter-vals some?))))


(defn restrict-type-by-field-def [field-def types]
  (if field-def
    (let [data-type-supported-field-types (-> field-def :type form-schema/data-type->field-types set)]
      (filter data-type-supported-field-types types))
    types))


(defn restrict-type-by-schema-type [schema-type field-types]
  (let [child-types-by-schema-type (schema-type-specific-child-types)
        types-for-this-schema      (get child-types-by-schema-type schema-type)
        all-restricted-types       (->> child-types-by-schema-type vals (apply set/union))
        forbidden-types            (set/difference all-restricted-types types-for-this-schema)]
    (remove forbidden-types field-types)))


(defn form-el->changeable-types [parent-type {:keys [type new-field? existing-field]} schema-type tables-enabled?]
  (->> (if new-field?
         (cond-> (vec (get-in config-for-types [parent-type :possible-child-types]))
           (and tables-enabled? (= parent-type :form-row))
           (conj :table))
         (get-in config-for-types [type :equivalent-types]))
       (into [type])
       distinct
       (restrict-type-by-field-def existing-field)
       (restrict-type-by-schema-type schema-type)))


(defn required-fields-filled?
  ([form-element] (required-fields-filled? false form-element))
  ([test-children-for-container-types? {:keys [type attributes] :as form-element}]
   (let [required-attrs          (-> (set (editable-attrs-for-type type))
                                     (set/intersection (set required-attrs-when-present))
                                     (set/union (-> config-for-types type :required-attrs set)))
         children-to-test        (when (or test-children-for-container-types?
                                           (-> (set container-types)
                                               (contains? type)
                                               not))
                                   (form-editor-util/form-el->children form-element))
         condition-field-filled? (fn [condition]
                                   (->> condition :field (p/remove-vals nil?) not-empty))]
     (and (->> required-attrs
               (every? #(let [value (get attributes %)]
                          (cond
                            (string? value)     (-> value str/blank? not)
                            (sequential? value) (seq value)
                            :else               (some? value)))))
          (or (-> attributes :condition nil?)
              (if (->> attributes :condition vector?)
                (->> attributes :condition (every? condition-field-filled?))
                (->> attributes :condition condition-field-filled?)))
          (every? (partial required-fields-filled? test-children-for-container-types?)
                  children-to-test)))))


(defn find-conflicting-fields
  ([conflict-type form-element] (find-conflicting-fields nil conflict-type form-element))
  ([test-children? conflict-type form-element]
   (lazy-cat (some-> (get form-element conflict-type) vector)
             (some->> (and test-children?
                           (form-editor-util/form-el->children form-element))
                      (mapcat (partial find-conflicting-fields test-children? conflict-type))))))

(defn find-duplicate-field [field-key omitted-id {:keys [id attributes] :as form-element}]
  (or (when (and (not= id omitted-id)
                 (= (:field attributes) field-key))
        form-element)
      (->> form-element
           form-editor-util/form-el->children
           (some (partial find-duplicate-field field-key omitted-id)))))

(defn adjust-path-fn
  "Adjust (increment or decrement array index) editscript paths the compensate for prior edits."
  [type->f]
  (fn [path [edit-type path-that-was-edited-earlier]]
    (let [;; the array index in the editscript that needs adjustment
          ix-to-adjust   (last path-that-was-edited-earlier)
          ;; ix in the path vector for the item that needs to be adjusted
          nth-to-inc     (dec (count path-that-was-edited-earlier))
          ;; the value in the path that is adjusted
          item-to-adjust (get path nth-to-inc)
          adjustment-op  (type->f edit-type)]
      (if (and
            ;; is this path in the same structure as the edit?
            (= (take nth-to-inc path)
               (take nth-to-inc path-that-was-edited-earlier))
            ;; is the item an array index? (as opposed to map key)
            (number? item-to-adjust)
            (number? ix-to-adjust)
            ;; does the path we are adjusting refer to an item later in the array
            ;; than the earlier edit operation? (e.g. if an item was added in position
            ;; 3, position 4 would need to be adjusted but 1 would not)
            (>= item-to-adjust ix-to-adjust)
            adjustment-op)
        (update path nth-to-inc adjustment-op)
        path))))

(defn apply-deletes
  "editscript edits refer a path after previous edits. We don't actually delete things, so
   we need to alter the path every time there are prior deletes in the editscript output"
  [edits path]
  (reduce (adjust-path-fn {:- inc}) path edits))

(defn apply-adds-and-deletes
  "When a diff adds things, the later editscript edits assume the thing now exists in the target
   structure. This is not true in our case when we access the old structure - that one will not
   contain the items added to the target. Therefore, indices need to be adjusted, but only when
   accessing the old structure."
  [edits path]
  (->> edits
       (sort-by first)
       (reduce (adjust-path-fn {:+ dec :- inc}) path)))

(defn get-changes [original-data data]
  (-> (e/diff data original-data)
      e/get-edits))

(defn path-before-kw [path]
  (-> (->> path
           (filter keyword?)
           first
           (.indexOf path))
      (take path)
      vec))

(defn- apply-diff-fn [old-data]
  (fn [{:keys [edits acc]} [original-path edit-type _new-val]]
    (let [;; the adjusted path for the new structure at `:acc`, taking into account that earlier edits didn't
          ;; actually delete anything
          path                 (apply-deletes edits original-path)
          ;; the adjusted path for the `old-data`, taking into account that previous deletes didn't delete and
          ;; previous adds didn't add anything
          path-at-old          (apply-adds-and-deletes edits original-path)
          ;; the change in question only affects a single property, not a whole schema element
          prop-only?           (-> path last keyword?)
          ;; is this an operation on a structured list row? (implies that the operation is an add or delete)
          structured-list-row? (= (first (get-in acc (drop-last path)))
                                  :structured-list)]
      {;; :edits attempt to keep track of the edit indices in relation to the structures we operate with:
       ;; instead of deleting items, we are actually just marking them deleted. Also, we are accessing items in the
       ;; pre-edit version. Therefore, we need to make two separate adjusted versions of each editscript path that
       ;; takes the earlier edits into account - the deletes when accessing the new structure and the adds when
       ;; accessing the old structure.
       :edits (cond-> edits
                (#{:- :+} edit-type) (conj [edit-type path]))
       :acc   (cond-> acc
                (and (= edit-type :-)
                     (> (count path) 1)
                     (not prop-only?)) ; add missing item from current data
                (assoc-in (-> path drop-last vec)
                          (insert (get-in acc (-> path drop-last vec))
                                  (last path)
                                  (get-in old-data path-at-old)))

                (and (= edit-type :-)
                     (= (count path) 1)) ; if path is in root don't assoc
                (insert (last path) (get-in old-data path-at-old))

                structured-list-row? ; structured list rows don't have the property map that we want to mark the diff operation into
                (update-in path
                           (fn [row]
                             (mapv #(assoc-in % [1 :diff] {:type edit-type})
                                   row)))

                (not structured-list-row?) ; add diff type flag
                (assoc-in
                  (cond (some keyword? path) ; if path contains keyword attributes are in the previous item
                        (conj (path-before-kw path) :diff)

                        (or (= edit-type :+)
                            (= edit-type :-)) ; if type is add or remove attributes are in the second item
                        (conj path 1 :diff)

                        :else
                        (conj (-> path drop-last vec) 1 :diff))
                  {:type (if prop-only?
                           ;; if a property was added or removed, that is still only a
                           ;; replacement on the element level
                           :r
                           edit-type)}))})))


(defn combine-changes
  "Adds metadata for the diff tool to the schema data hiccup
  and combines missing items from current schema data.

  diff types are :- (deletion), :+ (addition), :r (data replacement) or :s (string edit)

  Returns e.g.
  [:form {}
   [:section {}
    [:form-row {}
     [:p {:diff {:type :r}} 'Some text']]]
   [:section {:diff {:type :-}}]]"
  [new-data data]
  (let [changes (get-changes new-data data)]
    (->> changes
         (reduce (apply-diff-fn data)
                 {:acc   new-data
                  :edits []})
         :acc)))


(defn is-authority-form-schema? [{:form.schema/keys [form-required-write-permissions]}]
  (contains? form-required-write-permissions :permission.form/authority-write))


(defn attributes-defined?
  "Check if the form-element has a non-nil value for any of given attributes"
  [{:keys [attributes]} attrs]
  (some #(some? (get attributes %)) (set attrs)))


(defn field-key-max-length-exceeded? [field-key]
  (> (count field-key) field-key-max-length))

(defn- extract-field-maps [field-map]
  (some->> field-map
           (reduce (fn [acc [k v]]
                     (conj acc {k v}))
                   [])))

(defn extract-fields [c]
  (if (map? c)
    (extract-field-maps (:field c))
    (reduce (fn [acc item]
              (some->> (:field item)
                       extract-field-maps
                       (into acc)))
            []
            c)))

(defn and-condition? [c]
  (and (map? c)
       (= 1 (count c))
       (> (-> c extract-fields count) 1)))

(defn single-condition? [c]
  (and (map? c)
       (-> c extract-fields count (= 1))))

(defn or-condition?
  [c]
  (and (sequential? c)
       (seq c)
       (every? #(and (map? %)
                     (= (count %) 1)
                     (map? (:field %))
                     (= (count (:field %)) 1))
               c)))

(defn empty-condition?
  [c]
  (or (empty? c)
      (and (map? c)
           (empty? (:field c)))))

(defn group-condition [c]
  (let [condition-type   (cond
                           (and-condition? c) :AND
                           (single-condition? c) :SINGLE
                           (or-condition? c) :OR
                           (empty-condition? c) :NIL
                           :else :complex)
        extracted-fields (->> c
                              extract-fields
                              set)]
    {:condition-type   condition-type
     :extracted-fields extracted-fields
     :extracted-keys   (set (mapcat keys extracted-fields))}))


(defn save-condition [{:keys [condition]}
                      {:keys [cond-type new-cond-field new-cond-value]}]
  (cond
    (= cond-type :AND) (update condition :field assoc new-cond-field new-cond-value)
    (= cond-type :OR) (let [conds (if (map? condition)
                                    [condition]
                                    condition)]
                        (conj conds {:field {new-cond-field new-cond-value}}))
    :else {:field {new-cond-field
                   new-cond-value}}))

(defn delete-condition [{:keys [condition]} field]
  (cond
    (map? condition)
    (update condition :field dissoc field)

    (vector? condition)
    (let [remaining-clauses (->> condition
                                 (map #(update % :field dissoc field))
                                 (remove #(empty? (:field %)))
                                 vec)]
      (case (count remaining-clauses)
        0 {:field {}}
        1 (first remaining-clauses)
        remaining-clauses))

    ;; Absent/unsupported shapes normalize to the canonical empty condition so
    ;; update-db-after-delete can keep :condition omitted instead of writing nil.
    :else {:field {}}))

(defn update-db-after-delete [db {:keys [path field editor-data]}]
  (let [attr-path    (->> (concat editor-data path [:attributes])
                          vec)
        attributes   (get-in db attr-path)
        updated-cond (delete-condition attributes field)]
    (assoc-in db attr-path (if (and (empty? (:field updated-cond))
                                    (= 1 (count updated-cond)))
                             (dissoc attributes :condition)
                             (assoc attributes :condition updated-cond)))))

(defn values-for-condition-field [{:keys [type] :as form-field}]
  (case type
    :select (-> form-field :attributes :form.field/valid-values)
    :checkbox [true false]
    :radio-button-group (->> form-field
                             (p/filter-keys number?)
                             (mapv (comp :value :attributes second)))
    []))

(defn extract-attribute-fields
  "Extracts unique :field values from an element and its child elements."
  [m]
  (if (map? m)
    (->> (concat (some-> (get-in m [:attributes :field]) vector)
                 (mapcat extract-attribute-fields
                         (form-editor-util/form-el->children m)))
         distinct
         vec)
    []))

(defn find-condition-paths [m]
  (mapcat (fn [[_ v]]
            (let [p (:path v)]
              (concat
                (when (get-in v [:attributes :condition])
                  [p])
                (when (map? v)
                  (find-condition-paths v)))))
          m))
